import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFinancialWidgets } from '@/hooks/use-financial-widgets';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState } from 'react';

interface AssetValueTrendCardProps {
  readonly className?: string;
}

interface CustomTooltipPayloadEntry {
  readonly color: string;
  readonly name: string;
  readonly value: number;
  readonly payload?: { count?: number };
}

interface CustomTooltipProps {
  readonly active?: boolean;
  readonly payload?: CustomTooltipPayloadEntry[];
  readonly label?: string;
}

const CustomTooltipComponent = ({ active, payload, label }: CustomTooltipProps) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (active && payload?.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
        <p className="font-semibold mb-2">{label}</p>
        {payload.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4 text-sm">
            <span style={{ color: entry.color }}>{entry.name}:</span>
            <span className="font-semibold">{formatCurrency(entry.value)}</span>
          </div>
        ))}
        {payload[0]?.payload?.count && (
          <p className="text-xs text-muted-foreground mt-1">
            {payload[0].payload.count} assets
          </p>
        )}
      </div>
    );
  }
  return null;
};

export function AssetValueTrendCard({ className }: AssetValueTrendCardProps): React.ReactElement {
  const [months, setMonths] = useState<number>(12);
  const { useAssetValueTrend } = useFinancialWidgets();
  const { data, isLoading, error } = useAssetValueTrend(months);

  if (isLoading) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Asset Value Trend</CardTitle>
          <CardDescription>Value and depreciation over time</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-80 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.trendData.length === 0) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Asset Value Trend</CardTitle>
          <CardDescription>Value and depreciation over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">No trend data available.</div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.trendData.map((point: { month: string; value: number; depreciation: number }) => ({
    month: point.month,
    value: point.value,
    depreciation: point.depreciation,
  }));

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black', className)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Asset Value Trend</CardTitle>
            <CardDescription>Value and depreciation over time</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={months.toString()}
              onValueChange={(value: string) => setMonths(parseInt(value))}
            >
              <SelectTrigger className="h-7 w-[100px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">6 Months</SelectItem>
                <SelectItem value="12">12 Months</SelectItem>
                <SelectItem value="24">24 Months</SelectItem>
              </SelectContent>
            </Select>
            <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="month"
              className="text-xs"
              tick={{ fill: 'currentColor' }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'currentColor' }}
              tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltipComponent />} />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="value"
              name="Total Value"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="depreciation"
              name="Depreciation"
              stroke="#ef4444"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
